import {
	BillingAddress,
	BillingBusiness,
	CartItem,
	Settings,
} from "@version2/domain-package";
import { connectFunctionsEmulator, getFunctions } from "firebase/functions";
import type { MessageInstance } from "@version2/ui-package";
import { initFirebase } from "..";
import env from "../../../Consts/config/env";
import { apiPost } from "../../api";

initFirebase();

const functions = getFunctions();
if (env.IS_LOCAL) {
	connectFunctionsEmulator(
		functions,
		"localhost",
		parseInt(env.FUNCTIONS_EMULATOR_PORT)
	);
}

const subscribeToMailingList = async (
	message: MessageInstance,
	email: string,
	l10n: any
) => {
	const { subscribeToMailingList: apiSubscribe } = await import("../../api/mailing_list");
	return apiSubscribe(message, email, l10n);
};

const updateUserAddOnSettings = async (
	userAddOnId: string,
	settings: Settings[]
) => {
	const { updateUserAddOnSettings: apiUpdate } = await import("../../api/add_ons");
	return apiUpdate(userAddOnId, settings);
};

const updateSubscription = async (
	message: MessageInstance,
	items: CartItem[],
	internalSubscriptionId: string,
	checkoutId: string
) => {
	const { updateSubscription: apiUpdate } = await import("../../api/checkout");
	return apiUpdate(message, items, internalSubscriptionId, checkoutId);
};

const previewUpdateSubscription = async (
	message: MessageInstance,
	items: CartItem[],
	internalSubscriptionId: string
) => {
	const { previewUpdateSubscription: apiPreview } = await import("../../api/checkout");
	return apiPreview(message, items, internalSubscriptionId);
};

const getCreditBalance = async (message: MessageInstance) => {
	const { getCreditBalance: apiGet } = await import("../../api/billing");
	return apiGet(message);
};

const getSubscriptionDetails = async (
	message: MessageInstance,
	internalSubscriptionId: string
) => {
	const { getSubscriptionDetails: apiGet } = await import("../../api/billing");
	return apiGet(message, internalSubscriptionId);
};

const updateBillingAddress = async (
	message: MessageInstance,
	address: BillingAddress
) => {
	const { updateBillingAddress: apiUpdate } = await import("../../api/billing");
	return apiUpdate(message, address);
};

const updateBillingBusiness = async (
	message: MessageInstance,
	business: BillingBusiness,
	internalSubscriptionId: string
) => {
	const { updateBillingBusiness: apiUpdate } = await import("../../api/billing");
	return apiUpdate(message, business, internalSubscriptionId);
};

const getTransactionUpdateDetails = async (
	message: MessageInstance,
	internalSubscriptionId: string
) => {
	const { getTransactionUpdateDetails: apiGet } = await import("../../api/billing");
	return apiGet(message, internalSubscriptionId);
};

const cancelSubscription = async (
        message: MessageInstance,
        internalSubscriptionId: string
) => {
	const { cancelSubscription: apiCancel } = await import("../../api/billing");
	return apiCancel(message, internalSubscriptionId);
};

const getTransactionInvoice = async (
	message: MessageInstance,
	transactionId: string
) => {
	const { getTransactionInvoice: apiGet } = await import("../../api/billing");
	return apiGet(message, transactionId);
};

const getSubscriptionTransactions = async (
	message: MessageInstance,
	internalSubscriptionId: string
) => {
	const { getSubscriptionTransactions: apiGet } = await import(
		"../../api/billing"
	);
	return apiGet(message, internalSubscriptionId);
};

const getSubscriptionAdjustments = async (
	message: MessageInstance,
	internalSubscriptionId: string
) => {
	const { getSubscriptionAdjustments: apiGet } = await import(
		"../../api/billing"
	);
	return apiGet(message, internalSubscriptionId);
};

type LogAnalyticsParams = {
	eventName: string;
	params?: Record<string, unknown>;
	clientId?: string;
	sessionId?: string;
};

const logAnalyticsEvent = async ({
	eventName,
	params,
	clientId,
	sessionId,
}: LogAnalyticsParams) => {
	try {
		const payload: Record<string, unknown> = {
			event_name: eventName,
		};
		if (params) {
			payload.params = params;
		}
		if (clientId) {
			payload.client_id = clientId;
		}
		if (sessionId) {
			payload.session_id = sessionId;
		}

		await apiPost("/v1/analytics/events", payload, false);
	} catch (error) {
		console.error("Failed to log analytics event", error);
	}
};

export {
	functions,
	subscribeToMailingList,
	updateUserAddOnSettings,
	updateSubscription,
	getSubscriptionDetails,
	getTransactionInvoice,
	getSubscriptionTransactions,
	getSubscriptionAdjustments,
	getCreditBalance,
	previewUpdateSubscription,
	getTransactionUpdateDetails,
	cancelSubscription,
	updateBillingAddress,
	updateBillingBusiness,
	logAnalyticsEvent,
};
